/**
 * The recipient's side of "Compartir": a link `/viewer/?share=<id>&gateway=core` made by staff for someone
 * without a hub account. The viewer asks ris for the share's ticket (`POST /ris/v1/public/shares/{id}/open`),
 * after the access code when the link has one, and keeps it in memory only. ris checks the share on every
 * request, so a revoked or expired link stops the viewer at once.
 */

import type { ViewerShell } from '../shell';
import { openDialog, paragraph } from '../ui/dialogs';

export const SHARE_OPEN_TEXT = {
  codeTitle: 'Código de acceso',
  codeIntro: 'Este enlace está protegido. Escriba el código de acceso que le entregaron.',
  codeLabel: 'Código',
  codeWrong: 'El código no es correcto. Revíselo e intente de nuevo.',
  open: 'Abrir',
  cancel: 'Cancelar',
  unavailable: 'Este enlace no existe, ya venció o fue revocado. Pida uno nuevo a quien lo compartió.',
  failed: 'No fue posible abrir el enlace. Intente de nuevo en unos minutos.',
} as const;

const OPEN_PATH = (shareId: string) => `/ris/v1/public/shares/${encodeURIComponent(shareId)}/open`;
const CODE_REQUIRED = 'errors.shares.code_required';
const HTTP_UNAUTHORIZED = 401;
const HTTP_NOT_FOUND = 404;
const MAX_CODE = 40;

let ticket: string | null = null;

/** The share's ticket (null before the link was opened). */
export function currentTicket(): string | null {
  return ticket;
}

/** The link cannot be used (no such share, expired, revoked, or locked after wrong codes). */
export class ShareUnavailable extends Error {}

type Answer = { kind: 'opened'; studyUIDs: string[] } | { kind: 'code' };

async function ask(risRoot: string, shareId: string, code: string | null): Promise<Answer> {
  const response = await fetch(`${risRoot.replace(/\/$/, '')}${OPEN_PATH(shareId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(code === null ? {} : { code }),
  });
  if (response.status === HTTP_NOT_FOUND) throw new ShareUnavailable();
  if (response.status === HTTP_UNAUTHORIZED) {
    const body = (await response.json().catch(() => ({}))) as { detail?: unknown };
    if (body.detail === CODE_REQUIRED) return { kind: 'code' };
    throw new ShareUnavailable();
  }
  if (!response.ok) throw new Error(`share open ${response.status}`);
  const body = (await response.json()) as { ticket: string; study_uids: string[] };
  ticket = body.ticket;
  return { kind: 'opened', studyUIDs: body.study_uids };
}

/** Asks for the access code in page; null when the recipient cancels. */
function promptCode(shell: ViewerShell, wrong: boolean): Promise<string | null> {
  return new Promise((resolve) => {
    let answered = false;
    const input = document.createElement('input');
    input.type = 'password';
    input.autocomplete = 'off';
    input.maxLength = MAX_CODE;
    input.className = 'share-code-input';
    input.setAttribute('aria-label', SHARE_OPEN_TEXT.codeLabel);
    const finish = (value: string | null, close: () => void) => {
      answered = true;
      close();
      resolve(value);
    };
    const { body, dialog, close } = openDialog(shell, {
      title: SHARE_OPEN_TEXT.codeTitle,
      className: 'share-code-dialog',
      buttons: [
        { label: SHARE_OPEN_TEXT.cancel, onClick: (done) => finish(null, done) },
        { label: SHARE_OPEN_TEXT.open, primary: true, onClick: (done) => finish(input.value.trim() || null, done) },
      ],
    });
    paragraph(body, SHARE_OPEN_TEXT.codeIntro);
    if (wrong) paragraph(body, SHARE_OPEN_TEXT.codeWrong, 'share-code-wrong');
    body.appendChild(input);
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') finish(input.value.trim() || null, close);
    });
    // Esc / backdrop close the dialog without a button: that is a cancel.
    new MutationObserver((_records, observer) => {
      if (!dialog.isConnected) {
        observer.disconnect();
        if (!answered) resolve(null);
      }
    }).observe(document.body, { childList: true, subtree: true });
    input.focus();
  });
}

/**
 * Opens the shared exam: its studies, or null when the recipient gave up on the code. Throws
 * ``ShareUnavailable`` for a link that cannot be used.
 */
export async function openShare(shell: ViewerShell, risRoot: string, shareId: string): Promise<string[] | null> {
  let answer = await ask(risRoot, shareId, null);
  let wrong = false;
  while (answer.kind === 'code') {
    const code = await promptCode(shell, wrong);
    if (code === null) return null;
    answer = await ask(risRoot, shareId, code);
    wrong = true;
  }
  return answer.studyUIDs;
}
